import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, X } from "lucide-react";
import LeadTimeline from "./LeadTimeline";
import { useCanManageLeads } from "./permissions";

// Copied from AdminDashboard.jsx:207-215 — the other tabs still use that one.
function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500 col-span-1">{label}</span>
      <span className="col-span-2 text-sm text-slate-800 break-words">{String(value)}</span>
    </div>
  );
}

export default function LeadDetailModal({ leadId, onClose }) {
  const canManage = useCanManageLeads();
  const [lead, setLead] = useState(null);
  const reload = () => api.get(`/admin/leads/${leadId}`).then(({ data }) => setLead(data)).catch(() => onClose());
  useEffect(() => { reload(); }, [leadId]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="lead-detail-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{lead?.name || "Lead"}</h3>
            <p className="text-sm text-slate-500">{lead?.mobile}{lead?.email ? ` · ${lead.email}` : ""}</p>
            {lead && (
              <div className="flex gap-2 mt-2">
                <span className="nc-chip">{lead.source}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${lead.status === "assigned" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{lead.status}</span>
                {lead.is_deleted && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">deleted</span>
                )}
                {/* The detail response carries `siblings`, not `duplicate_count`
                    (that lives on the list rows) — so derive the total here. */}
                {lead.siblings?.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    ⚠ {lead.siblings.length + 1} leads from this number
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!lead ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-5">
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Details</h4>
              <div className="bg-slate-50 rounded-xl p-4">
                <DetailRow label="State / District" value={[lead.state, lead.district].filter(Boolean).join(" / ") || "—"} />
                <DetailRow label="Created" value={lead.created_at && new Date(lead.created_at).toLocaleString()} />
                <DetailRow label="Plan of interest" value={lead.plan ? `${lead.plan.title}` : null} />
                <DetailRow label="Message" value={lead.message} />
              </div>
            </section>
            {lead.plot_details && Object.keys(lead.plot_details).length > 0 && (
              <section>
                <h4 className="font-semibold text-slate-900 mb-2">Plot details</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  {Object.entries(lead.plot_details).map(([k, v]) => (
                    <DetailRow key={k} label={k.replace(/_/g, " ")} value={typeof v === "object" ? JSON.stringify(v) : String(v)} />
                  ))}
                </div>
              </section>
            )}
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Assigned partners ({lead.assignments.length})</h4>
              {lead.assignments.length === 0 ? (
                <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">Not assigned to any partner yet.</p>
              ) : (
                <div className="space-y-2">
                  {lead.assignments.map(a => (
                    <div key={a.id} className="border border-slate-200 rounded-xl p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-slate-900">{a.partner_name || "—"}</div>
                          <div className="text-xs text-slate-500">{[a.partner_mobile, a.partner_email].filter(Boolean).join(" · ")}</div>
                          <div className="text-xs text-slate-500 mt-1">{[a.partner_state, a.partner_district].filter(Boolean).join(" / ")}</div>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{a.status}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">Assigned {a.assigned_at && new Date(a.assigned_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {lead.siblings?.length > 0 && (
              <section data-testid="lead-siblings">
                <h4 className="font-semibold text-slate-900 mb-2">
                  Other enquiries from {lead.mobile}
                </h4>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-1">
                  {lead.siblings.map(s => (
                    <div key={s.id} className="text-sm text-slate-700">
                      {new Date(s.created_at).toLocaleDateString("en-IN")} · {s.name} · {s.source} · {s.status}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Work timeline</h4>
              <LeadTimeline leadId={lead.id} canManage={canManage} onChanged={reload} />
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
